import Vue from 'vue'
import isEqual from 'lodash.isequal'
import uuid from 'core/utils/uuid'
import * as types from 'store/mutation-types'
import { availableLanguages } from 'app/config'
import searchForKeyInObject from 'core/utils/searchForKeyInObject'
import searchKeyEqualToValueInObject from 'core/utils/searchKeyEqualToValueInObject'
import generateFileName from 'core/utils/generateFileName'
import { EARLY_RENEWAL_COMPONENT_NAME } from 'core/components/EarlyRenewal/consts'
import { removeBumpFromPayloadRows } from 'core/utils/removeBumpFromPayloadRows'
import { resolveTemplateKeyFromPayloadItem } from 'core/utils/previewAffiliation'
import log from 'app/log.js'

const defaultRow = () => {
  return {
    id: uuid(),
    columns: [
      {
        id: uuid(),
        width: 3,
        components: []
      }
    ]
  }
}

export default {
  [types.SET_TEMPLATE_COLUMNS_MODE] ({ templates, currentTemplate }, { columns, index }) {
    if (!isEqual(templates[currentTemplate].rows[index].columns, columns)) {
      templates[currentTemplate].rows[index].columns = columns
    }
  },
  [types.ADD_TEMPLATE_LINE] ({ templates, currentTemplate }) {
    templates[currentTemplate].rows.push(defaultRow())
  },
  [types.SET_TEMPLATE_ROW_POSITION] ({ templates, currentTemplate }, { oldIndex, newIndex }) {
    templates[currentTemplate].rows.splice(newIndex, 0, templates[currentTemplate].rows.splice(oldIndex, 1)[0])
  },
  [types.REMOVE_TEMPLATE_ROW] ({ templates, currentTemplate }, index) {
    templates[currentTemplate].rows.splice(index, 1)
  },
  [types.INSERT_TEMPLATE_ROW_BEFORE] ({ templates, currentTemplate }, index) {
    const beforeRowIndex = index === 0 ? 0 : index

    templates[currentTemplate].rows.splice(beforeRowIndex, 0, defaultRow())
  },
  [types.INSERT_TEMPLATE_ROW_AFTER] ({ templates, currentTemplate }, index) {
    const afterRowIndex = index + 1

    templates[currentTemplate].rows.splice(afterRowIndex, 0, defaultRow())
  },
  [types.CLONE_TEMPLATE_ROW] ({ templates, currentTemplate }, index) {
    let newRow = JSON.parse(JSON.stringify(templates[currentTemplate].rows[index]))

    newRow.id = uuid()

    templates[currentTemplate].rows.splice(index, 0, newRow)
  },
  [types.SET_CANVAS_INSERT_MODE] (state, mode) {
    state.insertMode = mode
  },
  [types.SET_CANVAS_EDIT_MODE] (state, { status, targetComponent, componentSource }) {
    state.editMode = status || false
    state.targetComponent = targetComponent
    state.componentSource = componentSource
  },
  [types.SET_CURRENT_COMPONENT] (state, { componentBody, name }) {
    state.currentComponent = componentBody
    state.currentComponentName = name
  },
  [types.ADD_SLOT_COMPONENT] ({ templates, currentTemplate }, component) {
    const { slot } = component

    if (templates[currentTemplate].slotComponents[slot]) {
      delete templates[currentTemplate].slotComponents[slot]
      templates[currentTemplate].slotComponentsCount = Object.keys(templates[currentTemplate].slotComponents).length
    }

    templates[currentTemplate].slotComponents[slot] = component
    templates[currentTemplate].slotComponentsCount = Object.keys(templates[currentTemplate].slotComponents).length
  },
  [types.REMOVE_SLOT_COMPONENT] ({ templates, currentTemplate }, component) {
    delete templates[currentTemplate].slotComponents[component]
    templates[currentTemplate].slotComponentsCount = Object.keys(templates[currentTemplate].slotComponents).length
  },
  [types.ADD_COMPONENT_TO_ROW_AND_COLUMN] ({ templates, currentTemplate }, { row, column, currentComponent }) {
    currentComponent.id = uuid()
    templates[currentTemplate].rows[row].columns[column].components.push(currentComponent)
  },
  [types.TEXT_EDITOR_INLINE_EDIT] ({ templates, currentTemplate }, { row, column, component, content }) {
    templates[currentTemplate].rows[row].columns[column].components[component].data.content = content
  },
  [types.TEXT_EDITOR_INLINE_EDIT_RAW] ({ templates, currentTemplate }, { row, column, component, rawContent }) {
    templates[currentTemplate].rows[row].columns[column].components[component].data.rawContent = rawContent
  },
  [types.SET_BUILDER_LOADING] (state, data) {
    state.builderLoading = {
      show: data?.show,
      message: data?.message || null
    }
  },
  [types.CLEAR_STATE_MODES] (state) {
    state.targetComponent = null
    state.currentComponent = null
    state.currentComponentName = null
    state.componentSource = null
    state.editMode = false
    state.insertMode = false
  },
  [types.EDIT_COMPONENT] ({ templates, currentTemplate, componentSource }, componentData) {
    const { row, column, component } = componentSource
    const targetColumn = templates[currentTemplate].rows[row].columns[column]

    if (targetColumn) {
      const { components } = templates[currentTemplate].rows[row].columns[column]

      components.splice(component, 1, componentData)
    }
  },
  [types.CLONE_COMPONENT] ({ templates, currentTemplate, imageList }, componentData) {
    try {
      const { row, column, component } = componentData
      const { components } = templates[currentTemplate].rows[row].columns[column]
      const data = {
        ...JSON.parse(JSON.stringify(components[component])),
        id: uuid()
      }
      const imagesInComponent = Object.keys(data.data).filter((key) => !!data.data[key]?.fileName)

      imagesInComponent.forEach((componentImage) => {
        if (imageList[data.data[componentImage].fileName]) {
          const image = {...data.data[componentImage], fileName: generateFileName(data.data[componentImage].fileName)}

          imageList[image.fileName] = {
            keepSize: image.keepSize,
            fileName: image.fileName,
            contentType: image.contentType,
            field: image.fileName,
            imageBase64: image.file
          }
          data.data[componentImage] = image
        }
      })

      components.splice(component, 0, data)
    } catch (error) {
      console.error('Failed to clone component:', error)
      log({
        error,
        action: 'CLONE_COMPONENT_FAILED',
        componentData,
        currentTemplate
      })
    }
  },
  [types.DELETE_COMPONENT] ({ templates, currentTemplate, componentSource }) {
    const { row, column, component } = componentSource
    const { components } = templates[currentTemplate].rows[row].columns[column]

    components.splice(component, 1)
  },
  [types.UPDATE_DRAG_COMPONENT] (state, { newIndexes, oldIndexes }) {
    if (
      newIndexes &&
      oldIndexes &&
      typeof oldIndexes.row !== 'undefined' &&
      typeof oldIndexes.column !== 'undefined' &&
      typeof oldIndexes.component !== 'undefined'
    ) {
      const rows = state.templates[state.currentTemplate].rows
      const targetColumn = rows[newIndexes.row].columns[newIndexes.column]
      const originColumn = rows[oldIndexes.row].columns[oldIndexes.column]

      if (targetColumn && originColumn) {
        let targetComponents = targetColumn.components
        let originComponents = originColumn.components
        const componentName = originColumn.components[oldIndexes.component].name

        Vue.analytics('send', {
          hitType: 'event',
          eventCategory: 'Build Checkout',
          eventAction: 'clone-component',
          eventLabels: `Component ${componentName.replace('hotpay-', '')}`
        })

        if (newIndexes.row === oldIndexes.row && newIndexes.column === oldIndexes.column) {
          targetComponents.splice(newIndexes.component, 0, targetComponents.splice(oldIndexes.component, 1)[0])
        } else {
          if (targetComponents.length) {
            targetComponents.splice(newIndexes.component, 0, originComponents[oldIndexes.component])
          } else {
            targetComponents.push(originComponents[oldIndexes.component])
          }

          originComponents.splice(oldIndexes.component, 1)
        }
      }
    }
  },
  [types.ADD_IMAGE] (state, newImage) {
    if (!newImage.file) return
    const imagesUrlList = state.currentImages?.map(image => image.componentLayoutFileResponseVO.webPath) ?? []
    const imageIsNetherNewOrChanged = imagesUrlList.find((image) => image === newImage.file)
    if (imageIsNetherNewOrChanged) return
    state.imageList[newImage.fileName] = {
      keepSize: newImage.keepSize,
      fileName: newImage.fileName,
      contentType: newImage.contentType,
      field: newImage.fileName,
      imageBase64: newImage.file
    }
  },
  [types.REMOVE_IMAGES] (state, data) {
    const images = searchForKeyInObject('fileName', data)
    images.forEach(image => delete state.imageList[image])
  },
  [types.CLEAR_IMAGES] (state) {
    state.imageList = {}
  },
  [types.STORE_CURRENT_IMAGES] (state, currentImages) {
    state.currentImages = state.currentImages.concat(currentImages)
  },
  [types.SAVE_TEMPLATE_NAME] (state, name) {
    state.checkoutName = name
  },
  [types.SAVE_CHECKOUT_FONT] ({ templates, currentTemplate }, font) {
    templates[currentTemplate].font = font
  },
  [types.SAVE_CHECKOUT_COLOR] ({ templates, currentTemplate }, color) {
    templates[currentTemplate].fontColor = color
  },
  [types.SAVE_CHECKOUT_BACKGROUND_COLOR] ({ templates, currentTemplate }, background) {
    templates[currentTemplate].background = background
  },
  [types.SAVE_CHECKOUT_BACKGROUND_IMAGE] ({ templates, currentTemplate }, backgroundImage) {
    if (backgroundImage) {
      templates[currentTemplate].backgroundImage = backgroundImage
      templates[currentTemplate].backgroundFixed = backgroundImage.fixed
      templates[currentTemplate].backgroundRepeat = backgroundImage.repeat
      templates[currentTemplate].backgroundCover = backgroundImage.cover
    }
  },
  [types.LOAD_DEFAULT_CHECKOUT] (state, checkoutName) {
    const checkoutPlatforms = ['desktop', 'mobile']

    checkoutPlatforms.forEach(platform => {
      availableLanguages.forEach(language => {
        state.templates[platform + '-' + language] = {
          rows: []
        }
      })
    })

    state.currentPlatform = 'mobile'
    state.currentLanguage = 'pt-BR'
    state.currentTemplate = state.currentPlatform + '-' + state.currentLanguage

    if (checkoutName) {
      state.checkoutName = checkoutName
    }

    state.checkoutDescription = ''
    state.imageList = {}
    state.currentImages = []
    state.templates[state.currentTemplate] = {
      checkout: false,
      slotComponents: {},
      slotComponentsCount: 0,
      inCheckoutComponents: [],
      font: {
        name: 'Open Sans',
        url: 'Open+Sans:400,400i,700,700i,800',
        family: '\'Open Sans\', sans-serif'
      },
      fontColor: '#000',
      background: '#fff',
      backgroundImage: {
        file: null
      },
      backgroundFixed: false,
      backgroundRepeat: false,
      backgroundCover: false,
      rows: [
        {
          id: uuid(),
          columns: [
            {
              id: uuid(),
              width: 3,
              components: []
            }
          ]
        }
      ]
    }

    let checkoutRow = {
      id: uuid(),
      columnGrid: ['3', '2-1'],
      columns: [
        {
          id: uuid(),
          width: 3,
          components: [
            {
              id: uuid(),
              name: 'hotpay-checkout',
              draggable: false,
              editable: false,
              static: true
            }
          ]
        }
      ]
    }

    if (state.currentPlatform === 'desktop') {
      checkoutRow.columns[0].width = 2
      checkoutRow.columns.push({
        id: uuid(),
        width: 1,
        components: []
      })
    }

    state.templates[state.currentTemplate].rows.push(checkoutRow)
    state.templates[state.currentTemplate].checkout = true
  },
  [types.LOAD_CURRENT_CHECKOUT] (state, { payload, name, description }) {
    const result = {}

    Object.keys(payload).forEach(key => {
      const template = payload[key]
      const { newRows, bumpsOutOfForm } = removeBumpFromPayloadRows(template.rows, { moveBumpsInsideForm: true })
      result[key] = {
        ...template,
        rows: newRows,
        inCheckoutComponents: [ ...template.inCheckoutComponents, ...bumpsOutOfForm ]
      }
    })

    state.templates = result
    state.checkoutName = name
    state.checkoutDescription = description
  },
  [types.COPY_MOBILE_CHECKOUT_TO_DESKTOP] ({ templates, currentLanguage }, newCheckout) {
    templates['desktop-' + currentLanguage] = newCheckout
  },
  [types.SET_ACTIVE_ROW] (state, row) {
    state.activeRow = row
  },
  [types.SET_ACTIVE_COLUMN] (state, column) {
    state.activeColumn = column
  },
  [types.SET_CURRENT_PLATFORM] (state, platform) {
    state.currentPlatform = platform
    state.currentTemplate = platform + '-' + state.currentLanguage
  },
  [types.SET_LAST_INSERT_COMPONENT] (state, { row, column, currentComponent }) {
    const lastComponent = {
      component: currentComponent,
      position: {
        template: state.currentTemplate,
        row,
        column,
        index: state.templates[state.currentTemplate].rows[row].columns[column].components.length - 1
      }
    }

    state.lastInsertComponent = lastComponent
  },
  [types.CLEAR_LAST_INSERT_COMPONENT] (state) {
    state.lastInsertComponent = {
      components: {},
      position: {
        template: null,
        row: null,
        col: null
      }
    }
  },
  [types.SET_BUILDER_CHECKOUT_STATUS] (state, status) {
    state.checkoutStatus = status
  },
  [types.SET_LAST_UPDATED_PLATFORM] (state, lastPlatform) {
    const setLastPlatform = typeof lastPlatform !== 'undefined' ? lastPlatform : JSON.parse(JSON.stringify(state.currentPlatform))
    state.lastUpdatedPlatform = setLastPlatform
  },
  [types.SET_CHECKOUT_HISTORY] (state, history) {
    state.checkoutHistory = JSON.parse(JSON.stringify(history))
  },
  [types.SET_DESKTOP_CHECKOUT] (state, newCheckout) {
    state.templates['desktop-' + state.currentLanguage] = JSON.parse(JSON.stringify(newCheckout))
  },
  [types.SET_CURRENT_EDIT_FORM] (state, slug) {
    state.currentEditForm = slug
  },
  [types.CLEAR_BUILDER_DATA] (state) {
    state.targetComponent = null
    state.currentComponent = null
    state.currentComponentName = null
    state.componentSource = null
    state.editMode = false
    state.insertMode = false
    state.checkoutName = null
    state.checkoutDescription = null
    state.checkoutStatus = ''
    state.currentPlatform = 'desktop'
    state.currentLanguage = 'pt-BR'
    state.currentTemplate = 'desktop-pt-BR'
    state.lastUpdatedPlatform = null
    state.currentEditForm = null
    state.templateHasUnsavedChanges = null
    state.skipRouteInit = false
    state.previewInProgress = false
  },
  [types.SET_TEMPLATE_UNSAVED_CHANGES] (state, status) {
    state.templateHasUnsavedChanges = status
  },
  [types.SET_UNSAVED_WARNING] (state, { show, logout = false }) {
    state.unsavedWarning = {
      show,
      logout
    }
  },
  [types.SET_ORDER_BUMP_DATA] (state, data) {
    state.orderBumpData = data
  },
  [types.SET_ORDER_BUMP_CUSTOM_IMAGE] (state, data) {
    state.orderBumpData = {
      ...state.orderBumpData,
      customImage: {
        ...data
      }
    }
  },
  [types.ADD_IN_CHECKOUT_COMPONENT] ({ templates, currentTemplate }, component) {
    templates[currentTemplate].inCheckoutComponents.push(component)
  },
  [types.REMOVE_IN_CHECKOUT_COMPONENT] ({ templates, currentTemplate }, inCheckoutId) {
    const { inCheckoutComponents } = templates[currentTemplate]
    const componentIndex = inCheckoutComponents.findIndex(item => item.id === inCheckoutId)
    inCheckoutComponents.splice(componentIndex, 1)
  },
  [types.EDIT_IN_CHECKOUT_COMPONENT] ({ templates, currentTemplate }, componentData) {
    const { inCheckoutComponents } = templates[currentTemplate]

    templates[currentTemplate].inCheckoutComponents = inCheckoutComponents.map(inCheckoutComponent => {
      if (inCheckoutComponent.id !== componentData.id) { return inCheckoutComponent }

      return componentData
    })
  },
  [types.SET_ROW_EDIT_MODE] (state, editMode) {
    state.rowEditMode = editMode
  },
  [types.SET_JUST_PUBLISHED_CHECKOUT] (state, checkout) {
    state.justPublishedCheckout = checkout
  },
  [types.SET_LAYOUT_IS_COPIED] (state, value) {
    state.layoutIsCopied = value
  },
  [types.SET_PREVIEW_WAS_OPEN] (state, value) {
    state.previewWasOpen = value
  },
  [types.SET_PRODUCT_PLANS_EARLY_RENEWAL_DATA] (state, data) {
    state.productPlansEarlyRenewal = data
  },
  [types.SET_EARLY_RENEWAL_DATA] (state, data) {
    state.earlyRenewalData = data
  },
  [types.SET_CURRENT_PRODUCT_EARLY_RENEWAL] (state, templates) {
    let currentProductEarlyRenewal = null

    if (templates) {
      const hotpayEarlyRenewal = searchKeyEqualToValueInObject({
        keyToSearch: 'name',
        equalsToValue: EARLY_RENEWAL_COMPONENT_NAME,
        objectToSearch: templates
      })

      currentProductEarlyRenewal = hotpayEarlyRenewal.find(Boolean).data.product
    }

    state.currentProductEarlyRenewal = currentProductEarlyRenewal
  },
  [types.SET_PRODUCTS_WITH_ENRICHED_DATA] (state, newProducts) {
    state.productsWithEnrichedData = { ...newProducts }
  },
  [types.CLEAR_PRODUCTS_WITH_ENRICHED_DATA] (state) {
    state.productsWithEnrichedData = { }
  },
  [types.SET_SKIP_ROUTE_INIT] (state, value) {
    state.skipRouteInit = value
  },
  [types.SET_PREVIEW_IN_PROGRESS] (state, value) {
    state.previewInProgress = Boolean(value)
  },
  [types.APPLY_CHECKOUT_LAYOUT_IDS] (state, payloadList = []) {
    payloadList.forEach((item) => {
      if (!item?.id) {
        return
      }

      const templateKey = resolveTemplateKeyFromPayloadItem(state.templates, item)

      if (!templateKey || !state.templates[templateKey]) {
        return
      }

      state.templates[templateKey] = {
        ...state.templates[templateKey],
        editMode: item.id
      }
    })
  }
}
